// AppFiltersModal - Mobile filter modal for app section
// Contains Sort by, Favorites, Owned dropdowns + Tags (same controls as AppSearchBar)

import React, { useState } from 'react';
import { RevoltModal, DropdownSelect, RevoltButton, RevoltSwitch } from '../../../components';
import './AppFiltersModal.css';

function normalizeTag(str) {
  return str.trim().toLowerCase().replace(/\s+/g, '-');
}

const SORT_OPTIONS = [
  { value: 'name', label: 'Name' },
  { value: 'lastModified', label: 'Modified' },
  { value: 'created', label: 'Created' },
];

const FAVORITES_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'favorites', label: 'Favorites only' },
  { value: 'nonFavorites', label: 'Non-favorites only' },
];

const OWNED_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'owned', label: 'Owned only' },
  { value: 'nonOwned', label: 'Non-owned only' },
];

export function AppFiltersModal({
  isOpen,
  onClose,
  sortBy = 'lastModified',
  onSortChange,
  favoritesFilter = 'all',
  onFavoritesChange,
  ownedFilter = 'all',
  onOwnedChange,
  tags = [],
  onTagsChange,
  onClearQuery,
  pinFavoritesTop = false,
  onPinFavoritesTopChange,
}) {
  const [tagInput, setTagInput] = useState('');

  const handleAddTag = () => {
    const normalized = normalizeTag(tagInput);
    if (normalized && !tags.includes(normalized)) {
      onTagsChange?.([...tags, normalized]);
      setTagInput('');
    }
  };

  const handleTagKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    onTagsChange?.(tags.filter((t) => t !== tagToRemove));
  };

  const handleReset = () => {
    onClearQuery?.();
    onSortChange?.('lastModified');
    onFavoritesChange?.('all');
    onOwnedChange?.('all');
    onTagsChange?.([]);
    onPinFavoritesTopChange?.(false);
    setTagInput('');
  };

  return (
    <RevoltModal isOpen={isOpen} onClose={onClose} title="Filters">
      <div className="app-filters-modal">
        <div className="app-filters-modal__group">
          <label htmlFor="app-filters-sort" className="app-filters-modal__label">
            Sort by
          </label>
          <DropdownSelect
            id="app-filters-sort"
            value={sortBy}
            options={SORT_OPTIONS}
            onChange={(e) => onSortChange?.(e.target.value)}
            className="app-filters-modal__select"
            menuClassName="app-management-sort-menu"
          />
        </div>
        <div className="app-filters-modal__group">
          <label htmlFor="app-filters-favorites" className="app-filters-modal__label">
            Favorites
          </label>
          <DropdownSelect
            id="app-filters-favorites"
            value={favoritesFilter}
            options={FAVORITES_OPTIONS}
            onChange={(e) => onFavoritesChange?.(e.target.value)}
            className="app-filters-modal__select"
            menuClassName="app-management-sort-menu"
          />
        </div>
        <div className="app-filters-modal__group">
          <label htmlFor="app-filters-owned" className="app-filters-modal__label">
            Owned
          </label>
          <DropdownSelect
            id="app-filters-owned"
            value={ownedFilter}
            options={OWNED_OPTIONS}
            onChange={(e) => onOwnedChange?.(e.target.value)}
            className="app-filters-modal__select"
            menuClassName="app-management-sort-menu"
          />
        </div>
        <div className="app-filters-modal__group">
          <label htmlFor="app-filters-tags" className="app-filters-modal__label">
            Tags
          </label>
          <input
            id="app-filters-tags"
            type="text"
            placeholder="Add tags (press Enter)"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={handleTagKeyDown}
            className="app-filters-modal__tag-input"
          />
          <div className="app-filters-modal__tags">
            {tags.map((tag) => (
              <button
                key={tag}
                type="button"
                className="app-filters-modal__tag"
                onClick={() => handleRemoveTag(tag)}
                aria-label={`Remove ${tag} tag`}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>
        {onPinFavoritesTopChange ? (
          <div className="app-filters-modal__group app-filters-modal__group--switch">
            <RevoltSwitch
              id="app-filters-pin-favorites-top"
              checked={pinFavoritesTop}
              onChange={(next) => onPinFavoritesTopChange(!!next)}
              label="Pin favorites to top"
            />
          </div>
        ) : null}
        <div className="app-filters-modal__actions">
          <RevoltButton variant="ghost" size="sm" onClick={handleReset}>
            Reset filters
          </RevoltButton>
        </div>
      </div>
    </RevoltModal>
  );
}
